import axios, {
  type AxiosError,
  type AxiosHeaderValue,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
} from 'axios';
import qs from 'qs';
import { getLocalApiBaseUrlOverride } from './getLocalApiBaseUrlOverride.ts';
import { languageInterceptor } from './interceptors/languageInterceptor.ts';
import { retryInterceptor } from './interceptors/retryInterceptor.tsx';

declare module 'axios' {
  export interface AxiosRequestConfig<D = any> {
    meta?: {
      hasIntlSupport?: boolean;
      serviceName?: string;
    };
  }
}

export const apiClient = axios.create({
  // Configurato direttamente negli entrypoint applicativi
  // baseURL: joinPath([appConfig.apiBaseUrl]),
  withCredentials: true,
  paramsSerializer: {
    serialize: (params: any) =>
      qs.stringify(params, { arrayFormat: 'brackets' }),
  },
});

languageInterceptor(apiClient);
retryInterceptor(apiClient);

export interface IApiClientRequestOptions {
  baseURL?: string;
  headers?: Record<string, AxiosHeaderValue>;
  // Permette di formattare gli array in modo che le API li parsino correttamente
  arrayFormat?: 'brackets' | 'repeat';
  responseType?: 'arraybuffer';
  fullResponse?: boolean;
  meta?: AxiosRequestConfig['meta'];
}

export async function apiClientRequest<T>(
  config: AxiosRequestConfig,
  options?: IApiClientRequestOptions,
): Promise<T> {
  const serializer = options?.arrayFormat
    ? {
        paramsSerializer: {
          serialize: (params: any) =>
            qs.stringify(params, {
              arrayFormat: options?.arrayFormat ?? 'brackets',
            }),
        },
      }
    : {};

  const localBaseURL = getLocalApiBaseUrlOverride(config.url);

  const { data, headers } = await apiClient.request({
    ...config,
    ...(localBaseURL ? { baseURL: localBaseURL } : {}),
    ...(options?.baseURL ? { baseURL: options.baseURL } : {}),
    responseType: options?.responseType,
    headers: {
      ...config.headers,
      ...options?.headers,
    },
    meta: { ...options?.meta },
    ...serializer,
  });
  // HACK per avere accesso ai Response Headers in data
  if (options?.fullResponse) {
    return { data, headers } as any;
  }
  return data;
}

export type ErrorType<Error> = AxiosError<Error>;

/**
 * Utility per parsare la risposta completa di una chiamata
 * con apiClientRequest in modalità `fullResponse`. Al momento
 * il tipo di ritorno è errato per poter accedere agli headers
 * e la gestione del risultato è manuale.
 */
export function parseApiClientFullResponse<T>(data: T) {
  if (!data) return { data: undefined, headers: undefined };
  const { data: _data, headers } = data as unknown as {
    data: any;
    headers: AxiosRequestHeaders;
  };

  return { data: _data, headers };
}
